export interface BybitPriceData {
  symbol: string;
  price: number;
  bid: number;
  ask: number;
  change24h: number;
  high24h: number;
  low24h: number;
  volume24h: number;
  timestamp: number;
}

export type BybitPriceUpdateCallback = (prices: Map<string, BybitPriceData>) => void;

const WEBSOCKET_URL = 'wss://stream.bybit.com/v5/public/linear';
const RECONNECT_BASE_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const PING_INTERVAL = 20000;

const CRYPTO_SYMBOLS = [
  'BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT',
  'DOGEUSDT', 'ADAUSDT', 'AVAXUSDT', 'TRXUSDT', 'LINKUSDT',
  'DOTUSDT', 'MATICUSDT', 'UNIUSDT', 'LTCUSDT', 'BCHUSDT',
  'NEARUSDT', 'APTUSDT', 'FILUSDT', 'ATOMUSDT', 'ARBUSDT',
  'OPUSDT', 'INJUSDT', 'SUIUSDT', 'AAVEUSDT', 'MKRUSDT',
  'GRTUSDT', 'SANDUSDT', 'MANAUSDT', 'AXSUSDT', 'GALAUSDT',
  'SHIBUSDT', 'TONUSDT', 'ETCUSDT', 'HBARUSDT', 'XLMUSDT',
  'VETUSDT', 'THETAUSDT', 'ALGOUSDT', 'FLOWUSDT', 'CHZUSDT',
  'ENJUSDT', 'XTZUSDT', 'NEOUSDT', 'IOTAUSDT', 'QNTUSDT'
];

class BybitWebSocketService {
  private ws: WebSocket | null = null;
  private prices: Map<string, BybitPriceData> = new Map();
  private subscribers: Set<BybitPriceUpdateCallback> = new Set();
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private isConnecting = false;
  private connectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private statusListeners: Set<(status: string) => void> = new Set();

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    this.setStatus('connecting');

    try {
      this.ws = new WebSocket(WEBSOCKET_URL);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.setStatus('connected');
        this.subscribeToTickers();
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.setStatus('disconnected');
        this.stopPingInterval();
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        this.isConnecting = false;
        this.setStatus('disconnected');
      };
    } catch {
      this.isConnecting = false;
      this.setStatus('disconnected');
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPingInterval();

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = false;
    this.setStatus('disconnected');
  }

  private subscribeToTickers(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const batchSize = 10;
    for (let i = 0; i < CRYPTO_SYMBOLS.length; i += batchSize) {
      const batch = CRYPTO_SYMBOLS.slice(i, i + batchSize);
      const args = batch.map(symbol => `tickers.${symbol}`);

      const subscribeMsg = {
        op: 'subscribe',
        args: args
      };

      this.ws.send(JSON.stringify(subscribeMsg));
    }
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ op: 'ping' }));
      }
    }, PING_INTERVAL);
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      if (message.topic && message.topic.startsWith('tickers.') && message.data) {
        this.processTickerUpdate(message.data);
      }
    } catch {
    }
  }

  private processTickerUpdate(data: any): void {
    const symbol = data.symbol;
    if (!symbol) return;

    const price = parseFloat(data.lastPrice) || 0;
    const bid = parseFloat(data.bid1Price) || price * 0.9999;
    const ask = parseFloat(data.ask1Price) || price * 1.0001;
    const change24h = parseFloat(data.price24hPcnt) * 100 || 0;
    const high24h = parseFloat(data.highPrice24h) || 0;
    const low24h = parseFloat(data.lowPrice24h) || 0;
    const volume24h = parseFloat(data.volume24h) || 0;

    if (price > 0) {
      this.prices.set(symbol, {
        symbol,
        price,
        bid,
        ask,
        change24h,
        high24h,
        low24h,
        volume24h,
        timestamp: Date.now()
      });

      this.notifySubscribers();
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, Math.min(this.reconnectAttempts - 1, 10)),
      MAX_RECONNECT_DELAY
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private setStatus(status: 'disconnected' | 'connecting' | 'connected'): void {
    this.connectionStatus = status;
    this.statusListeners.forEach(listener => listener(status));
  }

  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(new Map(this.prices)));
  }

  subscribe(callback: BybitPriceUpdateCallback): () => void {
    this.subscribers.add(callback);

    if (this.prices.size > 0) {
      callback(this.prices);
    }

    return () => {
      this.subscribers.delete(callback);
    };
  }

  onStatusChange(callback: (status: string) => void): () => void {
    this.statusListeners.add(callback);
    callback(this.connectionStatus);

    return () => {
      this.statusListeners.delete(callback);
    };
  }

  getPrice(symbol: string): BybitPriceData | null {
    return this.prices.get(symbol) || null;
  }

  getMidPrice(symbol: string): number {
    const priceData = this.prices.get(symbol);
    return priceData?.price || 0;
  }

  getBidPrice(symbol: string): number {
    const priceData = this.prices.get(symbol);
    return priceData?.bid || 0;
  }

  getAskPrice(symbol: string): number {
    const priceData = this.prices.get(symbol);
    return priceData?.ask || 0;
  }

  getAllPrices(): Map<string, BybitPriceData> {
    return new Map(this.prices);
  }

  getStatus(): string {
    return this.connectionStatus;
  }

  isConnected(): boolean {
    return this.connectionStatus === 'connected';
  }
}

export const bybitWebSocket = new BybitWebSocketService();
